import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Tabs, TabPanel } from '../../components/ui/Tabs';
import { Alert } from '../../components/ui/Alert';
import { icons } from '../../components/ui/Icons';
import { PageHeader } from '../../components/layout/PageHeader';
import { Dialog } from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { formatDate, formatRelativeTime, requestJson, RequestError } from '../../lib/personnelApi';
import type { EmployeeDetail, EmployeeStatus, EmploymentType, Department, Position, UpdateEmployeeInput } from '../../types/employee';

const STATUS_CONFIG: Record<EmployeeStatus, { label: string; variant: 'primary' | 'medical' | 'emergency' | 'warning' | 'gray' | 'outline' }> = {
  AKTIV: { label: 'Aktiv', variant: 'medical' },
  INAKTIV: { label: 'Inaktiv', variant: 'gray' },
  BEURLAUBT: { label: 'Beurlaubt', variant: 'warning' },
  AUSGESCHIEDEN: { label: 'Ausgeschieden', variant: 'emergency' },
};

const EMPLOYMENT_TYPES = [
  { value: 'VOLLZEIT', label: 'Vollzeit' },
  { value: 'TEILZEIT', label: 'Teilzeit' },
  { value: 'MINIJOB', label: 'Minijob' },
  { value: 'AUSBILDUNG', label: 'Ausbildung' },
  { value: 'PRAKTIKUM', label: 'Praktikum' },
  { value: 'WERKSTUDENT', label: 'Werkstudent' },
];

const GENDER_OPTIONS = [
  { value: 'M', label: 'Männlich' },
  { value: 'W', label: 'Weiblich' },
  { value: 'D', label: 'Divers' },
];

const STATUS_OPTIONS = [
  { value: 'AKTIV', label: 'Aktiv' },
  { value: 'INAKTIV', label: 'Inaktiv' },
  { value: 'BEURLAUBT', label: 'Beurlaubt' },
  { value: 'AUSGESCHIEDEN', label: 'Ausgeschieden' },
];

interface FieldRowProps {
  label: string;
  value: string | null | undefined;
  icon?: React.ReactNode;
}

function FieldRow({ label, value, icon }: FieldRowProps) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
      {icon && <div className="flex-shrink-0 w-5 h-5 text-neutral-400" aria-hidden="true">{icon}</div>}
      <div className="min-w-0 flex-1">
        <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400 truncate">{label}</dt>
        <dd className="text-sm text-neutral-900 dark:text-white whitespace-normal break-words">{value}</dd>
      </div>
    </div>
  );
}

function FieldGroup({ title, children, icon }: { title: string; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <Card variant="bordered">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {icon && <span className="h-5 w-5 text-neutral-500" aria-hidden="true">{icon}</span>}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('stammdaten');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<UpdateEmployeeInput>({});
  const [saving, setSaving] = useState(false);

  const fetchEmployee = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await requestJson<EmployeeDetail>(`/employees/${id}`);
      setEmployee(result);
    } catch (err) {
      setError(err instanceof RequestError ? err.message : 'Fehler beim Laden der Mitarbeiterdaten');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchDepartments = useCallback(async () => {
    try {
      const result = await requestJson<Department[]>(`/employees/departments`);
      setDepartments(result);
    } catch {
      // ignore
    }
  }, []);

  const fetchPositions = useCallback(async () => {
    try {
      const result = await requestJson<Position[]>(`/employees/positions`);
      setPositions(result);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchEmployee();
    fetchDepartments();
    fetchPositions();
  }, [fetchEmployee, fetchDepartments, fetchPositions]);

  const handleEditOpen = () => {
    if (!employee) return;
    setEditForm({
      firstName: employee.firstName ?? undefined,
      lastName: employee.lastName ?? undefined,
      personnelNumber: employee.personnelNumber ?? undefined,
      departmentId: employee.departmentId ?? undefined,
      positionId: employee.positionId ?? undefined,
      hireDate: employee.hireDate ?? undefined,
      employmentType: employee.employmentType ?? undefined,
      phone: employee.phone ?? undefined,
      extension: employee.extension ?? undefined,
      email: employee.email ?? undefined,
      status: employee.status,
      dateOfBirth: employee.dateOfBirth ?? undefined,
      gender: employee.gender as 'M' | 'W' | 'D' | undefined,
      isActive: employee.isActive,
      terminationDate: employee.terminationDate ?? undefined,
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await requestJson(`/employees/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(editForm),
      });
      setEditDialogOpen(false);
      fetchEmployee();
    } catch (err) {
      setError(err instanceof RequestError ? err.message : 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: EmployeeStatus) => {
    if (!id || !employee) return;
    try {
      await requestJson(`/employees/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus, isActive: newStatus === 'AKTIV' }),
      });
      fetchEmployee();
    } catch (err) {
      setError(err instanceof RequestError ? err.message : 'Fehler beim Statuswechsel');
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl p-4 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl p-4 sm:p-6">
        <Alert variant="error" title="Fehler" dismissible onDismiss={() => setError(null)}>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="mx-auto max-w-7xl p-4 sm:p-6">
        <Alert variant="error" title="Nicht gefunden">
          <p>Mitarbeiter nicht gefunden.</p>
        </Alert>
      </div>
    );
  }

  const tabs = [
    { id: 'stammdaten', label: 'Stammdaten', icon: <icons.User className="h-4 w-4" /> },
    { id: 'beschaeftigung', label: 'Beschäftigung', icon: <icons.Briefcase className="h-4 w-4" /> },
    { id: 'kontakt', label: 'Kontakt', icon: <icons.Mail className="h-4 w-4" /> },
    { id: 'qualifikationen', label: 'Qualifikationen', icon: <icons.Star className="h-4 w-4" /> },
    { id: 'aktivitaet', label: 'Aktivität', icon: <icons.History className="h-4 w-4" /> },
  ];

  const statusConfig = STATUS_CONFIG[employee.status];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      <PageHeader
        title={employee.displayName}
        description={employee.personnelNumber ? `Mitarbeiter-Nr.: ${employee.personnelNumber}` : 'Keine Personalnummer'}
        breadcrumbs={[
          { label: 'Startseite', href: '/' },
          { label: 'Personal', href: '/personal' },
          { label: 'Mitarbeiter', href: '/personal/mitarbeiter' },
          { label: employee.displayName },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant={statusConfig.variant} className="mr-2">
              {statusConfig.label}
            </Badge>
            <Button variant="outline" leftIcon="Edit" onClick={handleEditOpen}>
              Bearbeiten
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} variant="default" />

          <TabPanel id="stammdaten" active={activeTab === 'stammdaten'}>
            <FieldGroup title="Stammdaten" icon={<icons.User className="h-5 w-5 text-neutral-500" />}>
              <div className="space-y-2">
                <FieldRow label="Vorname" value={employee.firstName} icon={<icons.User className="h-4 w-4" />} />
                <FieldRow label="Nachname" value={employee.lastName} icon={<icons.User className="h-4 w-4" />} />
                <FieldRow label="Geburtsdatum" value={employee.dateOfBirth ? formatDate(employee.dateOfBirth) : null} icon={<icons.Calendar className="h-4 w-4" />} />
                <FieldRow label="Geschlecht" value={employee.gender ? GENDER_OPTIONS.find(g => g.value === employee.gender)?.label : null} icon={<icons.User className="h-4 w-4" />} />
                <FieldRow label="Personalnummer" value={employee.personnelNumber} icon={<icons.Hash className="h-4 w-4" />} />
                <FieldRow label="Status" value={statusConfig.label} icon={<icons.Activity className="h-4 w-4" />} />
                <FieldRow label="Aktiv" value={employee.isActive ? 'Ja' : 'Nein'} icon={<icons.CheckCircle className="h-4 w-4" />} />
              </div>
            </FieldGroup>

            {employee.isLinkedToDiscord && (
              <FieldGroup title="Discord-Verknüpfung" icon={<icons.MessageSquare className="h-5 w-5 text-neutral-500" />}>
                <div className="space-y-2">
                  <FieldRow label="Discord-Benutzername" value={employee.discordUsername} icon={<icons.User className="h-4 w-4" />} />
                  <FieldRow label="Discord-Anzeigename" value={employee.discordDisplayName} icon={<icons.User className="h-4 w-4" />} />
                  <FieldRow label="Discord-ID" value={employee.discordUserId} icon={<icons.Hash className="h-4 w-4" />} />
                </div>
              </FieldGroup>
            )}
          </TabPanel>

          <TabPanel id="beschaeftigung" active={activeTab === 'beschaeftigung'}>
            <FieldGroup title="Beschäftigung" icon={<icons.Briefcase className="h-5 w-5 text-neutral-500" />}>
              <div className="space-y-2">
                <FieldRow label="Abteilung" value={employee.departmentName} icon={<icons.Building2 className="h-4 w-4" />} />
                <FieldRow label="Position" value={employee.position} icon={<icons.Briefcase className="h-4 w-4" />} />
                <FieldRow label="Eintrittsdatum" value={employee.hireDate ? formatDate(employee.hireDate) : null} icon={<icons.Calendar className="h-4 w-4" />} />
                <FieldRow label="Beschäftigungsart" value={employee.employmentType ? EMPLOYMENT_TYPES.find(t => t.value === employee.employmentType)?.label : null} icon={<icons.Clock className="h-4 w-4" />} />
                <FieldRow label="Austrittsdatum" value={employee.terminationDate ? formatDate(employee.terminationDate) : null} icon={<icons.Calendar className="h-4 w-4" />} />
              </div>
            </FieldGroup>
          </TabPanel>

          <TabPanel id="kontakt" active={activeTab === 'kontakt'}>
            <FieldGroup title="Kontakt" icon={<icons.Mail className="h-5 w-5 text-neutral-500" />}>
              <div className="space-y-2">
                <FieldRow label="Dienstliche E-Mail" value={employee.email} icon={<icons.Mail className="h-4 w-4" />} />
                <FieldRow label="Telefon" value={employee.phone} icon={<icons.Phone className="h-4 w-4" />} />
                <FieldRow label="Interne Durchwahl" value={employee.extension} icon={<icons.Phone className="h-4 w-4" />} />
              </div>
            </FieldGroup>
          </TabPanel>

          <TabPanel id="qualifikationen" active={activeTab === 'qualifikationen'}>
            <FieldGroup title="Qualifikationen" icon={<icons.Star className="h-5 w-5 text-neutral-500" />}>
              <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                <icons.Star className="mx-auto h-12 w-12 mb-3 text-neutral-300 dark:text-neutral-600" aria-hidden="true" />
                <p>Keine Qualifikationen erfasst.</p>
                <p className="text-sm mt-1">Dieser Bereich wird in einer späteren Version erweitert.</p>
              </div>
            </FieldGroup>
          </TabPanel>

          <TabPanel id="aktivitaet" active={activeTab === 'aktivitaet'}>
            <FieldGroup title="Änderungshistorie" icon={<icons.History className="h-5 w-5 text-neutral-500" />}>
              <div className="space-y-4">
                <div className="flex items-center gap-3 rounded-lg bg-neutral-50 dark:bg-neutral-900/40 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                    <icons.Edit className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900 dark:text-white">Mitarbeiter angelegt</p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      {formatRelativeTime(employee.createdAt)} · {employee.createdAt ? formatDate(employee.createdAt, true) : '-'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-neutral-50 dark:bg-neutral-900/40 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                    <icons.RefreshCw className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900 dark:text-white">Letzte Aktualisierung</p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      {formatRelativeTime(employee.updatedAt)} · {formatDate(employee.updatedAt, true)}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Detailliertes Audit-Log ist im Admin-Bereich unter <strong>System → Audit-Log</strong> einsehbar.
                </p>
              </div>
            </FieldGroup>
          </TabPanel>
        </div>

        <div className="space-y-6">
          <Card variant="bordered">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <icons.Info className="h-5 w-5 text-neutral-500" aria-hidden="true" />
                Schnellinfo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500 dark:text-neutral-400">Mitarbeiter-ID</span>
                <code className="font-mono text-neutral-900 dark:text-white bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 rounded">{employee.id}</code>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500 dark:text-neutral-400">Erstellt</span>
                <span className="font-medium text-neutral-900 dark:text-white">{formatDate(employee.createdAt, true)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500 dark:text-neutral-400">Zuletzt geändert</span>
                <span className="font-medium text-neutral-900 dark:text-white">{formatDate(employee.updatedAt, true)}</span>
              </div>
              {employee.lastLoginAt && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-500 dark:text-neutral-400">Letzter Login</span>
                  <span className="font-medium text-neutral-900 dark:text-white">{formatDate(employee.lastLoginAt, true)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card variant="bordered">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <icons.Activity className="h-5 w-5 text-neutral-500" aria-hidden="true" />
                Status ändern
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              {Object.entries(STATUS_CONFIG).map(([statusValue, config]) => (
                <Button
                  key={statusValue}
                  variant={employee.status === statusValue ? 'primary' : 'outline'}
                  className="w-full justify-start"
                  onClick={() => handleStatusChange(statusValue as EmployeeStatus)}
                  disabled={employee.status === statusValue}
                  leftIcon={employee.status === statusValue ? 'Check' : undefined}
                >
                  {config.label}
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="Mitarbeiter bearbeiten"
        description="Ändern Sie die Stammdaten des Mitarbeiters."
        size="lg"
        closeOnBackdrop={!saving}
        closeOnEscape={!saving}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Vorname <span className="text-red-500">*</span>
            </label>
            <Input
              value={editForm.firstName || ''}
              onChange={e => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
              required
              placeholder="Max"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Nachname <span className="text-red-500">*</span>
            </label>
            <Input
              value={editForm.lastName || ''}
              onChange={e => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
              required
              placeholder="Mustermann"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Personalnummer
            </label>
            <Input
              value={editForm.personnelNumber || ''}
              onChange={e => setEditForm(prev => ({ ...prev, personnelNumber: e.target.value }))}
              placeholder="z. B. 12345"
            />
          </div>
          <div>
            <Select
              label="Abteilung"
              value={editForm.departmentId || ''}
              onChange={e => setEditForm(prev => ({ ...prev, departmentId: e.target.value || undefined }))}
              options={[{ value: '', label: 'Abteilung wählen' }, ...departments.map(d => ({ value: d.id, label: d.name }))]}
            />
          </div>
          <div>
            <Select
              label="Position"
              value={editForm.positionId || ''}
              onChange={e => setEditForm(prev => ({ ...prev, positionId: e.target.value || undefined }))}
              options={[{ value: '', label: 'Position wählen' }, ...positions.map(position => ({ value: position.id, label: position.name }))]}
              placeholder="Position wählen"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Eintrittsdatum
            </label>
            <Input
              type="date"
              value={editForm.hireDate || ''}
              onChange={e => setEditForm(prev => ({ ...prev, hireDate: e.target.value || undefined }))}
            />
          </div>
          <div>
            <Select
              label="Beschäftigungsart"
              value={editForm.employmentType || ''}
              onChange={e => setEditForm(prev => ({ ...prev, employmentType: e.target.value as EmploymentType || undefined }))}
              options={[{ value: '', label: 'Beschäftigungsart wählen' }, ...EMPLOYMENT_TYPES]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Telefon
            </label>
            <Input
              value={editForm.phone || ''}
              onChange={e => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="+49 123 456789"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Durchwahl
            </label>
            <Input
              value={editForm.extension || ''}
              onChange={e => setEditForm(prev => ({ ...prev, extension: e.target.value }))}
              placeholder="z. B. 1234"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              E-Mail
            </label>
            <Input
              type="email"
              value={editForm.email || ''}
              onChange={e => setEditForm(prev => ({ ...prev, email: e.target.value }))}
              placeholder="max.mustermann@klinikum.de"
            />
          </div>
          <div>
            <Select
              label="Status"
              value={editForm.status || ''}
              onChange={e => setEditForm(prev => ({ ...prev, status: e.target.value as EmployeeStatus }))}
              options={STATUS_OPTIONS}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Geburtsdatum
            </label>
            <Input
              type="date"
              value={editForm.dateOfBirth || ''}
              onChange={e => setEditForm(prev => ({ ...prev, dateOfBirth: e.target.value || undefined }))}
            />
          </div>
          <div>
            <Select
              label="Geschlecht"
              value={editForm.gender || ''}
              onChange={e => setEditForm(prev => ({ ...prev, gender: e.target.value as 'M' | 'W' | 'D' || undefined }))}
              options={[{ value: '', label: 'Geschlecht wählen' }, ...GENDER_OPTIONS]}
            />
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editForm.isActive ?? true}
                onChange={e => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Mitarbeiter ist aktiv</span>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Austrittsdatum
            </label>
            <Input
              type="date"
              value={editForm.terminationDate || ''}
              onChange={e => setEditForm(prev => ({ ...prev, terminationDate: e.target.value || undefined }))}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6 border-t border-neutral-200 dark:border-neutral-700 pt-4">
          <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleEditSave} loading={saving} disabled={saving}>
            Speichern
          </Button>
        </div>
      </Dialog>
    </div>
  );
}